/**
 * @file 習い事・体験のスナップショット (StudyExploration) を取得する hook。
 *
 * @what: familyId ごとに 1 回読み、`ready` では未同期 (404) を `exploration: null` で表す。
 * @why: 読み取り専用の参照情報なので、plan の draft 機構とは独立に持つ。
 * @failure: 404 以外の失敗は `error` (画面は控えめな 1 行で知らせる。詳細は console のみ)。
 */

import { useEffect, useState } from "react";
import { fetchStudyExploration, type StudyExploration } from "../api/exploration";

export type StudyExplorationState =
  | { status: "loading" }
  | { status: "ready"; exploration: StudyExploration | null }
  | { status: "error" };

export function useStudyExploration(familyId: string): StudyExplorationState {
  const [state, setState] = useState<StudyExplorationState>({ status: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    fetchStudyExploration(familyId, controller.signal)
      .then((exploration) => {
        if (!controller.signal.aborted) {
          setState({ status: "ready", exploration });
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load exploration", err);
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId]);

  return state;
}
